import React, { useEffect, useRef, useState } from "react";

import {
  aggregate,
  buildKpiAllocation,
  buildBreakdown,
  classifyRecords,
  formatPeriodLabel,
  getBreakdownLabel,
  validateConfiguration,
} from "../model/calculations";
import { getBreakdownOptions } from "../model/defaults";
import { buildIssueReport } from "../model/issues";
import { ReportState, ReportingDataset } from "../model/types";
import BreakdownChart from "./BreakdownChart";
import DataQualityPanel from "./DataQualityPanel";
import KpiGrid from "./KpiGrid";
import TargetsPreview from "./TargetsPreview";

interface ReportPreviewProps {
  state: ReportState;
  dataset: ReportingDataset;
  workspaceUuid: string;
  onRefresh: () => void;
  isRefreshing: boolean;
}

function formatTimestamp(value: string | null): string {
  if (!value) return "not built yet";

  const date = new Date(value);
  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const dayDifference = Math.round(
    (startOfToday.getTime() - startOfDate.getTime()) / 86_400_000,
  );
  const time = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (dayDifference === 0) return `Today, ${time}`;
  if (dayDifference === 1) return `Yesterday, ${time}`;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * The report surface, ordered by how much the numbers matter.
 *
 * Headline figures and the comparison chart are the report; traceability and the record
 * drill-down are how you debug it when a figure looks wrong. So the first two are given
 * the space, and the latter two live behind a disclosure that advertises itself only when
 * there is actually something to look at.
 */
export default function ReportPreview(props: ReportPreviewProps) {
  const { state, dataset, workspaceUuid, onRefresh, isRefreshing } = props;
  const { configuration, view } = state;
  const refreshInProgress = isRefreshing || dataset.isStale;

  const classified = classifyRecords(dataset.records, state);
  const totals = aggregate(classified.eligible);
  const kpiAllocation = buildKpiAllocation(classified.eligible);
  const breakdown = buildBreakdown(classified.eligible, state);
  // Every record is assumed complete for a workspace that turns this off (e.g. a dataset
  // sourced from an already-reconciled finance import), so the score/contributing-record
  // breakdown below would only be noise.
  const issues = configuration.dataQualityEnabled
    ? buildIssueReport(classified, state)
    : null;
  const errors = validateConfiguration(state, dataset.fields);
  // Collapsed by default - this is debugging material, not the report. The badge on the
  // toggle carries the record count, so a less-than-perfect score is still visible without
  // opening the panel.
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const diagnosticsRef = useRef<HTMLElement>(null);
  const scrollToDiagnosticsOnOpen = useRef(false);
  const [showTargetsPreview, setShowTargetsPreview] = useState(false);
  const marketFilter = view.filters.market ?? [];

  const qualityPercent = issues ? Math.round(issues.score * 100) : 100;
  const hasAttention = Boolean(issues && issues.attentionCount > 0);

  useEffect(() => {
    if (!showDiagnostics || !scrollToDiagnosticsOnOpen.current) return;

    diagnosticsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    scrollToDiagnosticsOnOpen.current = false;
  }, [showDiagnostics]);

  const openDiagnosticsFromQuality = () => {
    if (showDiagnostics) {
      diagnosticsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    scrollToDiagnosticsOnOpen.current = true;
    setShowDiagnostics(true);
  };

  return (
    <section className="finance-reporting__preview" aria-live="polite">
      <div className="finance-reporting__preview-header">
        <div>
          <p className="finance-reporting-eyebrow">Live preview</p>
          <h2>{configuration.reportName || "Untitled financial report"}</h2>
          {configuration.description && (
            <p className="finance-reporting__preview-description">
              {configuration.description}
            </p>
          )}
        </div>
        <div className="finance-reporting__preview-meta">
          <div className="finance-reporting__snapshot-status">
            <span className="finance-reporting__snapshot-date">
              Last updated: {formatTimestamp(dataset.generatedAt)}
            </span>
            {refreshInProgress ? (
              <span className="finance-reporting__snapshot-building">
                This report is being updated and will refresh automatically when
                ready. The figures below may be out of date; this can take
                several minutes.
              </span>
            ) : (
              <span>{dataset.rowCount} source records</span>
            )}
          </div>
          {!configuration.disableFrontendRefreshAction && (
            <button
              type="button"
              className="finance-reporting-button finance-reporting-button--secondary finance-reporting__refresh-button"
              onClick={onRefresh}
              disabled={refreshInProgress}
            >
              {refreshInProgress ? (
                <>
                  Refreshing data
                  <span
                    className="finance-reporting__refresh-spinner"
                    aria-hidden="true"
                  />
                </>
              ) : (
                "Refresh data"
              )}
            </button>
          )}
        </div>
      </div>

      {errors.length > 0 && (
        <div className="finance-reporting__validation">{errors.join(" ")}</div>
      )}

      <div className="finance-reporting__pills">
        <span className="finance-reporting-pill">
          <strong>Period:</strong>{" "}
          {view.periods.length
            ? view.periods.map(formatPeriodLabel).join(", ")
            : "All dates"}
        </span>
        {getBreakdownOptions(configuration).map((option) => {
          const selected = view.filters[option.key] ?? [];
          if (!selected.length) return null;

          return (
            <span className="finance-reporting-pill" key={option.key}>
              <strong>{option.label}:</strong>{" "}
              {selected
                .map((value) =>
                  option.key === "month" && value !== "No date"
                    ? formatPeriodLabel(value)
                    : value,
                )
                .join(", ")}
            </span>
          );
        })}
        {configuration.targets.length > 0 && (
          <button
            type="button"
            className="finance-reporting-pill finance-reporting-pill--action"
            onClick={() => setShowTargetsPreview(true)}
          >
            View all targets
          </button>
        )}
      </div>
      {showTargetsPreview && (
        <TargetsPreview
          targets={configuration.targets}
          onClose={() => setShowTargetsPreview(false)}
        />
      )}
      {issues && (
        <button
          type="button"
          className={`finance-reporting__quality-row${
            hasAttention ? "" : " finance-reporting__quality-row--clean"
          }`}
          aria-expanded={showDiagnostics}
          aria-controls="finance-reporting-diagnostics-body"
          onClick={openDiagnosticsFromQuality}
        >
          <span className="finance-reporting__quality-label">
            Data completeness:
          </span>
          <strong>{qualityPercent}%</strong>
          <span>
            {issues.goodContributingCount} good contributing record
            {issues.goodContributingCount === 1 ? "" : "s"} ·{" "}
            {issues.attentionCount} record
            {issues.attentionCount === 1 ? "" : "s"} need attention
          </span>
        </button>
      )}

      {/* Primary: the figures themselves. */}
      <KpiGrid
        kpis={view.kpis}
        totals={totals}
        allocation={kpiAllocation}
        allocationLabels={configuration.allocationLabels}
        targets={configuration.targets}
        periods={view.periods}
        marketFilter={marketFilter}
      />

      {/* Primary: the comparison, given the full width it needs. */}
      <div className="finance-reporting-charts">
        <BreakdownChart
          title={`Revenue by ${getBreakdownLabel(state)}`}
          rows={breakdown}
          metric="revenue"
          allocationLabels={configuration.allocationLabels}
          breakdownKey={view.breakdown}
          targets={configuration.targets}
          marketFilter={marketFilter}
        />
        <BreakdownChart
          title={`Profit by ${getBreakdownLabel(state)}`}
          rows={breakdown}
          metric="profit"
          allocationLabels={configuration.allocationLabels}
          breakdownKey={view.breakdown}
          targets={configuration.targets}
          marketFilter={marketFilter}
        />
      </div>

      {/* Secondary: how to debug the figures above. */}
      {issues && (
        <section
          className="finance-reporting__diagnostics"
          ref={diagnosticsRef}
        >
          <button
            type="button"
            className="finance-reporting__diagnostics-toggle"
            aria-expanded={showDiagnostics}
            aria-controls="finance-reporting-diagnostics-body"
            onClick={() => setShowDiagnostics((open) => !open)}
          >
            <span className="finance-reporting__diagnostics-title">
              Data checks
            </span>
            <span
              className={
                qualityPercent === 100
                  ? "finance-reporting__diagnostics-badge finance-reporting__diagnostics-badge--clean"
                  : "finance-reporting__diagnostics-badge"
              }
            >
              {qualityPercent === 100
                ? "All records complete"
                : `${issues.attentionCount} record${
                    issues.attentionCount === 1 ? "" : "s"
                  } need attention`}
            </span>
            <span
              className="finance-reporting__diagnostics-chevron"
              aria-hidden="true"
            >
              {showDiagnostics ? "Hide" : "Show"}
            </span>
          </button>

          {showDiagnostics && (
            <div id="finance-reporting-diagnostics-body">
              <DataQualityPanel
                report={issues}
                configuration={configuration}
                fields={dataset.fields}
                workspaceUuid={workspaceUuid}
              />
            </div>
          )}
        </section>
      )}
    </section>
  );
}
